import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { FileText, Upload, Loader2 } from 'lucide-react';

interface Props {
  resumeUrl: string | null;
  professionalId: string;
  userId: string;
  onResumeUpdated: (url: string) => void;
}

export function ResumeCard({ resumeUrl, professionalId, userId, onResumeUpdated }: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast({ title: t('myProfile.fileTooLarge'), variant: 'destructive' });
      return;
    }

    setUploading(true);
    try {
      const ext = file.name.split('.').pop();
      const path = `resumes/${userId}_resume.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('uploads')
        .upload(path, file, { upsert: true });
      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from('uploads').getPublicUrl(path);

      const { error: dbError } = await supabase
        .from('professionals')
        .update({ resume_url: urlData.publicUrl })
        .eq('id', professionalId);
      if (dbError) throw dbError;

      onResumeUpdated(urlData.publicUrl);
      toast({ title: t('myProfile.resumeUploadSuccess') });
    } catch (err: any) {
      console.error(err);
      toast({ title: t('myProfile.resumeUploadError'), variant: 'destructive' });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <FileText className="h-5 w-5 text-primary" />
          {t('myProfile.resume')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {resumeUrl ? (
          <div className="flex items-center gap-4 flex-wrap">
            <Badge variant="outline" className="text-green-700 border-green-300 bg-green-50">
              {t('myProfile.resumeUploaded')}
            </Badge>
            <Button variant="outline" size="sm" asChild>
              <a href={resumeUrl} target="_blank" rel="noopener noreferrer">
                <FileText className="mr-2 h-4 w-4" />
                {t('myProfile.viewResume')}
              </a>
            </Button>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t('myProfile.noResume')}</p>
        )}

        <div className="flex items-center gap-3">
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.doc,.docx"
            onChange={handleUpload}
            className="hidden"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
            {resumeUrl ? t('myProfile.replaceResume') : t('myProfile.uploadResume')}
          </Button>
          <span className="text-xs text-muted-foreground">{t('myProfile.resumeFormats')}</span>
        </div>
      </CardContent>
    </Card>
  );
}
